import { StrictMode, useMemo } from 'react';
import ReactDOM from 'react-dom/client';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import {
  PentaApiClientProvider,
  LegacyAuthGatewayApiClientProvider,
} from '@penta/penta-api';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import {
  setupLegacyAuthClient,
  setupPentaApiClient,
} from './api/setupAuthClient';
import { isDevelopmentModeEnabled } from './utils/envUtils';

// Import the generated route tree
import { routeTree } from './routeTree.gen';

import './styles.scss';
import reportWebVitals from './reportWebVitals';

import { initI18n } from '@penta/i18n-init';
import { I18nextProvider } from 'react-i18next';
import {
  APP_COMMIT_DATE,
  APP_COMMIT_HASH,
  APP_VERSION,
} from '@penta/penta-shared';

const i18n = initI18n(APP_VERSION || APP_COMMIT_HASH);

declare global {
  interface Window {
    __APP_VERSION__?: string;
  }
}

// Expose version info
console.info(
  `%c Penta App %c ${APP_COMMIT_HASH?.substring(0, 7) ?? 'LOCAL'} %c ${APP_VERSION ?? '—'} %c ${APP_COMMIT_DATE ?? '—'} `,
  'background: #333; color: #fff; border-radius: 3px 0 0 3px; padding: 2px 5px',
  'background: #16a34a; color: #fff; padding: 2px 5px; font-weight: bold',
  'background: #2563eb; color: #fff; padding: 2px 5px; font-weight: bold',
  'background: #7c3aed; color: #fff; border-radius: 0 3px 3px 0; padding: 2px 5px',
);
window.__APP_VERSION__ = APP_COMMIT_HASH;

async function enableMocking() {
  if (!isDevelopmentModeEnabled()) {
    return;
  }

  const { initMocks } = await import('./initMocks');

  return initMocks();
}

// Create a new router instance
const router = createRouter({
  routeTree,
  // Context defined in routes/__root.tsx will be required here
  // We initialize with undefined! because we'll provide it via <RouterProvider context={{ auth }} />
  context: {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    auth: undefined!,
  },
  defaultPreload: 'intent',
  scrollRestoration: true,
  defaultStructuralSharing: true,
  defaultPreloadStaleTime: 0,
});

// Register the router instance for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

// Initialize QueryClient with global retry strategy
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount: number, error: unknown) => {
        // Don't retry more than 5 times
        if (failureCount >= 5) return false;

        // Validations for OpenAPI Fetch errors
        // Retry on network errors (no status) or server errors (5xx)
        const fetchError = error as { status?: number };
        if (!fetchError?.status || fetchError.status >= 500) {
          return true;
        }

        // Don't retry on client errors (4xx), e.g. 404, 401, 403
        return false;
      },
      // Exponential backoff: 1s, 2s, 4s, 8s, 16s... capped at 30s
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    },
  },
});

// Internal App component to inject auth context
function App() {
  const auth = useAuth();

  const routerContext = useMemo(() => ({ auth }), [auth]);

  // Initialize API Clients with logout callback
  // This allows the middleware to auto-logout on 401 responses
  const handleUnauthorized = () => {
    auth.logout();
    window.location.href = '/login';
  };

  const getLanguageForApi = () => i18n.language || 'en';

  const legacyAuthClient = setupLegacyAuthClient(
    queryClient,
    handleUnauthorized,
    getLanguageForApi,
  );
  const pentaApiClient = setupPentaApiClient(
    queryClient,
    handleUnauthorized,
    getLanguageForApi,
  );

  return (
    <LegacyAuthGatewayApiClientProvider
      rqClient={legacyAuthClient.rqClient}
      fetchClient={legacyAuthClient.fetchClient}
    >
      <PentaApiClientProvider client={pentaApiClient.rqClient}>
        <RouterProvider router={router} context={routerContext} />
      </PentaApiClientProvider>
    </LegacyAuthGatewayApiClientProvider>
  );
}

// Render the app
const rootElement = document.getElementById('root');
if (rootElement && !rootElement.innerHTML) {
  enableMocking().then(() => {
    const root = ReactDOM.createRoot(rootElement);
    root.render(
      <StrictMode>
        <AuthProvider>
          <QueryClientProvider client={queryClient}>
            <I18nextProvider i18n={i18n}>
              <App />
            </I18nextProvider>
          </QueryClientProvider>
        </AuthProvider>
      </StrictMode>,
    );
  });
}

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
